/**
 * Pool Reports Net Allocation Owner Details Page
 *
 * Displays vessels for a specific owner with their net allocation data.
 * Shows vessel-level breakdown of Balance Undistributed columns.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { useNetAllocationOwner } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { Button, Toggle } from '@/components/ui';
import { formatNumber } from '@/utils/formatters';
import { CSVService } from '@/utils/csvExport';
import type { NetAllocation } from '@/types/poolReportsExtended.types';
import {
  POOL_REPORT_VESSEL_COL_HEADER,
  poolReportTheadDrilldownClass,
  poolReportThEmphasisClass,
  poolReportTdPadClass,
} from '@/components/poolReports/poolReportTableChrome';

export function PoolReportsNetAllocationOwnerPage() {
  const navigate = useNavigate();
  const { ownerId } = useParams<{ ownerId: string }>();
  const [searchParams] = useSearchParams();
  const { reportType, setSubTitle } = usePoolReportsStore();

  const fileId = Number(searchParams.get('fileId'));
  const poolId = Number(searchParams.get('poolId'));
  const ownerNameParam = searchParams.get('ownerName');

  const {
    details,
    vessels = [],
    isLoading,
  } = useNetAllocationOwner(fileId, Number(ownerId), !!(fileId && ownerId));

  const ownerName = useMemo(() => {
    if (ownerNameParam) return ownerNameParam;

    const detailName =
      (details as Record<string, unknown> | undefined)?.ownerName ||
      (details as Record<string, unknown> | undefined)?.OwnerName ||
      (details as Record<string, unknown> | undefined)?.['Owner Name'];
    if (detailName && typeof detailName === 'string') return detailName;

    const vesselName = vessels.find(
      (vessel) =>
        (vessel as Record<string, unknown>).ownerName ||
        (vessel as Record<string, unknown>).OwnerName ||
        (vessel as Record<string, unknown>)['Owner Name']
    );

    if (vesselName) {
      const value =
        (vesselName as Record<string, unknown>).ownerName ||
        (vesselName as Record<string, unknown>).OwnerName ||
        (vesselName as Record<string, unknown>)['Owner Name'];
      if (typeof value === 'string') return value;
    }

    return 'Owner';
  }, [details, ownerNameParam, vessels]);

  // Set subtitle for navigation
  useEffect(() => {
    setSubTitle({
      value: `Net Allocation and Outstanding Balance: ${ownerName}`,
      backLink: `/reports/${reportType}/net-allocation`,
      queryParams: {
        poolId: poolId.toString(),
        fileId: fileId.toString(),
      },
    });

    return () => {
      setSubTitle(null);
    };
  }, [ownerName, reportType, poolId, fileId, setSubTitle]);

  const [showBalance, setShowBalance] = useState(false);
  const toggleLabel = showBalance ? 'Hide Prior Years Balance' : 'Show Prior Years Balance';

  // Process data: suppress columns with all values < 10 (except Prior Years)
  const processedData = vessels.length > 0 ? suppressLowValueColumns(vessels) : [];

  // Get columns from first row (exclude vesselId and withLink from display)
  const columns =
    processedData.length > 0
      ? Object.keys(processedData[0]).filter((key) => {
          const lowerKey = key.toLowerCase();
          return lowerKey !== 'vesselid' && lowerKey !== 'withlink';
        })
      : [];

  // Suppress columns where all vessel rows have values < 10
  // EXCEPT "Balance Undistributed Prior Years" which is always shown
  function suppressLowValueColumns(data: NetAllocation[]): NetAllocation[] {
    const columnsToCheck: string[] = [];
    Object.keys(data[0]).forEach((key) => {
      // Check Balance Undistributed year columns (but not "Prior Years")
      if (key.startsWith('Balance Undistributed') && !key.includes('Prior Years')) {
        columnsToCheck.push(key);
      }
    });

    const columnsToSuppress = columnsToCheck.filter((column) => {
      const hasSignificantValue = data
        .filter((row) => row.vesselId || row.VesselID) // Exclude total row
        .some((row) => {
          const value = row[column];
          return typeof value === 'number' && Math.abs(value) >= 10;
        });
      return !hasSignificantValue;
    });

    return data.map((row) => {
      const newRow = { ...row };
      columnsToSuppress.forEach((column) => {
        delete newRow[column];
      });
      return newRow;
    });
  }

  // Convert column name to CSS classes (space-separated words become multiple classes)
  function getColumnClass(columnName: string): string {
    // Convert to lowercase and normalize spaces
    return columnName.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  // Navigate to vessel details
  function handleVesselClick(vessel: NetAllocation) {
    const vesselId = vessel.vesselId || vessel.VesselID;
    const vesselName = vessel.name || vessel.Name;
    const withLink = vessel.withLink || vessel.WithLink;

    if (!withLink || !vesselId) return;

    navigate(
      `/reports/${reportType}/net-allocation/owner/${ownerId}/${vesselId}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(ownerName)}&vesselName=${encodeURIComponent(vesselName || '')}`
    );
  }

  // Export to CSV
  function handleExport() {
    if (processedData.length === 0) return;

    let cols = columns;

    // Filter columns based on showBalance
    if (!showBalance) {
      cols = columns.filter((col) => {
        const lower = col.toLowerCase();
        if (lower.includes('balance undistributed prior years')) return true;
        if (lower.includes('balance undistributed')) return false;
        return true;
      });
    }

    const rows = processedData.map((item) =>
      cols.map((col, index) => {
        const value = item[col];
        if (value === null || value === undefined) return '';
        if (index > 0) return formatNumber(value as number, 0);
        return String(value);
      })
    );

    CSVService.export(rows, `net-allocation-${ownerName}-vessels`, cols);
  }

  // Render loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
          <div className="h-4 w-64 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
        </div>

        {/* Controls skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-6 w-10 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
            <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
          </div>
          <div className="h-9 w-36 animate-pulse rounded-full bg-gray-200 dark:bg-dark-700"></div>
        </div>

        {/* Table skeleton */}
        <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm dark:border-dark-700">
          <div className="report-table-shell">
            <div className="min-w-[1200px] text-sm tabular-nums">
              <div className="grid grid-cols-12 bg-gray-50 dark:bg-dark-800">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="border-b border-gray-200 p-2.5 dark:border-dark-700 sm:p-3"
                  >
                    <div className="ml-auto h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                  </div>
                ))}
              </div>
              {[...Array(7)].map((_, i) => (
                <div key={i} className="grid grid-cols-12">
                  {[...Array(12)].map((_, j) => (
                    <div
                      key={j}
                      className="border-b border-gray-200 bg-white p-2.5 dark:border-dark-700 dark:bg-dark-900 sm:p-3"
                    >
                      <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-dark-700"></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* CSS for hiding columns */}
      <style>{`
        th.balance.undistributed:not(.prior),
        td.balance.undistributed:not(.prior) {
          display: none !important;
        }
        table.show-balance-details th.balance.undistributed,
        table.show-balance-details td.balance.undistributed {
          display: table-cell !important;
        }
      `}</style>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
        {/* Table Header with Controls and Export Button */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-dark-700">
          <div className="flex items-center gap-3">
            <h2 className="type-section-title">Vessel Details</h2>
            <div className="flex items-center gap-2">
              <Toggle
                id="showBalance"
                checked={showBalance}
                onChange={setShowBalance}
                disabled={isLoading}
              />
              <label
                htmlFor="showBalance"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {toggleLabel}
              </label>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || processedData.length === 0}
            className="rounded-full px-4 shadow-sm"
          >
            Export to Excel
          </Button>
        </div>

        <div className="report-table-shell">
          <table
            className={`net-allocation-table report-table relative ${showBalance ? 'show-balance-details' : ''}`}
          >
            <thead className={poolReportTheadDrilldownClass}>
              <tr>
                {columns.map((column, colIndex) => {
                  const cssClass = getColumnClass(column);
                  const isVesselColumn = colIndex === 0;
                  return (
                    <th
                      key={column}
                      className={clsx(
                        poolReportThEmphasisClass,
                        'break-words',
                        cssClass,
                        isVesselColumn &&
                          'sticky left-0 z-30 min-w-[200px] bg-gray-50 shadow-sm dark:bg-dark-800'
                      )}
                      style={{ minWidth: '150px', maxWidth: '200px' }}
                    >
                      {isVesselColumn ? POOL_REPORT_VESSEL_COL_HEADER : column}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {processedData.map((vessel, rowIndex) => {
                const isTotal = !(vessel.vesselId || vessel.VesselID);
                const isFirstRow = rowIndex === 0;
                const withLink = vessel.withLink || vessel.WithLink;

                return (
                  <tr
                    key={rowIndex}
                    className={isTotal ? '' : 'hover:bg-gray-50 dark:hover:bg-dark-800'}
                  >
                    {columns.map((column, colIndex) => {
                      const value = vessel[column];
                      const isVesselColumn = colIndex === 0;
                      const cssClass = getColumnClass(column);

                      return (
                        <td
                          key={column}
                          className={clsx(
                            poolReportTdPadClass,
                            'whitespace-nowrap tabular-nums text-gray-900 dark:text-white',
                            isFirstRow && 'border-t-2',
                            isTotal
                              ? 'border-t-2 border-gray-300 bg-gray-50 font-bold dark:border-dark-600 dark:bg-dark-800'
                              : 'border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900',
                            cssClass,
                            isVesselColumn &&
                              'sticky left-0 z-10 min-w-[200px] border-r border-gray-200 font-bold uppercase shadow-[8px_0_8px_-8px_rgba(0,0,0,0.1)] dark:border-dark-700'
                          )}
                        >
                          {isVesselColumn && value ? (
                            withLink ? (
                              <div
                                className="flex cursor-pointer items-center justify-end gap-2"
                                onClick={() => handleVesselClick(vessel)}
                              >
                                <span className="text-md font-semibold uppercase text-gray-900 underline hover:no-underline dark:text-white">
                                  {value}
                                </span>
                                <ExternalLink className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400" />
                              </div>
                            ) : (
                              <span>{value}</span>
                            )
                          ) : (
                            <span>
                              {typeof value === 'number' ? formatNumber(value, 0) : value || ''}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* No Data */}
      {!isLoading && processedData.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No vessel data available for this owner
          </p>
        </div>
      )}
    </div>
  );
}
